import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import {
  getUserByShortcode,
  calculateOptimalPace,
  calculateMentalResilienceWithHistory,
  convertFirebaseTimestamp,
  mergeInsatiableJournalEntriesWithDoryRows,
  getFoundersFirstJournalsForGroupsAdminBatch,
  getFoundersFirstUserLookupById,
  looksLikeInsatiableShortcode,
  entryTimestampToMillis
} from '../../services/funnelService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' 
    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)` 
    : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.text};
  border: ${props => props.variant === 'primary' ? 'none' : `2px solid ${theme.colors.primary}`};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}20;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
`;

const UserInfo = styled.div`
  background: ${theme.colors.neutral}05;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const UserInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.xs};
`;

const InfoValue = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};
`;

const ChartTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

const buildDisplayName = ({ firstName, lastName, name }) => {
  const fromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;
  if (name && String(name).trim()) return String(name).trim();
  return null;
};

const UserLookupScreen = () => {
  const [lookupInput, setLookupInput] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUserInfo, setShowUserInfo] = useState(true);

  const handleLookupByShortcode = async (normalizedShortcode) => {
    const data = await getUserByShortcode(normalizedShortcode);
    if (!data) {
      setError('No user found with that shortcode');
      return;
    }

    let ffBundle = { entries: [] };
    try {
      const ffByShortcode = await getFoundersFirstJournalsForGroupsAdminBatch([normalizedShortcode]);
      ffBundle = ffByShortcode[normalizedShortcode] || { entries: [] };
    } catch (ffError) {
      console.error('Founders-first journals failed:', ffError);
    }

    const mergedJournalEntries = mergeInsatiableJournalEntriesWithDoryRows(
      data.journalEntries || [],
      ffBundle.entries || []
    ).filter((entry) => Number.isFinite(entryTimestampToMillis(entry.timestamp)));

    setUserData({
      ...data,
      lookupSource: 'shortcode',
      journalEntries: mergedJournalEntries
    });
  };

  const handleLookupByFoundersFirstUserId = async (userId) => {
    const ffUser = await getFoundersFirstUserLookupById(userId);
    if (!ffUser?.found) {
      setError('No founders-first user found with that user ID');
      return;
    }

    let insatiableEntries = [];
    let insatiableUser = null;
    const linkedShortcode = ffUser.shortCode || null;
    if (linkedShortcode) {
      try {
        insatiableUser = await getUserByShortcode(linkedShortcode);
        insatiableEntries = insatiableUser?.journalEntries || [];
      } catch (insatiableError) {
        console.error('Insatiable journals for linked shortcode failed:', insatiableError);
      }
    }

    const mergedJournalEntries = mergeInsatiableJournalEntriesWithDoryRows(
      insatiableEntries,
      ffUser.entries || []
    ).filter((entry) => Number.isFinite(entryTimestampToMillis(entry.timestamp)));

    const displayName = buildDisplayName({
      firstName: ffUser.firstName,
      lastName: ffUser.lastName,
      name: ffUser.name
    });

    setUserData({
      ...(insatiableUser || {}),
      shortcode: linkedShortcode || null,
      foundersFirstUserId: ffUser.userId,
      lookupSource: 'founders-first',
      startTime: ffUser.createdOn != null ? convertFirebaseTimestamp(ffUser.createdOn) : null,
      userInfo: {
        ...(insatiableUser?.userInfo || {}),
        name: displayName || insatiableUser?.userInfo?.name || null,
        email: ffUser.email || insatiableUser?.userInfo?.email || null,
        phone: ffUser.phone || insatiableUser?.userInfo?.phone || null
      },
      journalEntries: mergedJournalEntries
    });
  };

  const handleLookup = async () => {
    const raw = lookupInput.trim();
    if (!raw) {
      setError('Please enter a shortcode or founders-first user ID');
      return;
    }

    setLoading(true);
    setError(null);
    setUserData(null);

    try {
      if (looksLikeInsatiableShortcode(raw)) {
        await handleLookupByShortcode(raw.toUpperCase());
      } else {
        await handleLookupByFoundersFirstUserId(raw);
      }
    } catch (err) {
      setError(`Error fetching user data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateOptimalPaceForEntry = (entry) => {
    if (!entry.emotionalStates || !entry.hoursWorked) {
      return null;
    }
    
    try {
      // Pass the entire entry object as expected by calculateOptimalPace
      const optimalPace = calculateOptimalPace(entry);
      return optimalPace.optimalHours;
    } catch (error) {
      console.error('Error calculating optimal pace for entry:', error);
      return null;
    }
  };

  const calculateMentalResilienceForEntry = (entries, currentIndex) => {
    // Use up to 15 previous entries for calculation
    const startIndex = Math.max(0, currentIndex - 14);
    const relevantEntries = entries.slice(startIndex, currentIndex + 1);
    
    try {
      const resilience = calculateMentalResilienceWithHistory(relevantEntries);
      return resilience.score;
    } catch (error) {
      console.error('Error calculating mental resilience for entry:', error);
      return null;
    }
  };

  // Billing cycle: renewal day from stripeDetails.createdAt; Stripe uses last day of month when renewal day doesn't exist (e.g. 31 in Feb).
  const getRenewalDay = (createdAt) => {
    const d = convertFirebaseTimestamp(createdAt) || (createdAt != null ? new Date(createdAt) : null);
    if (!d || isNaN(d.getTime())) return null;
    return d.getDate();
  };

  const getLastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

  // End date of the most recent completed billing cycle (inclusive). Stripe: renewal on day R; months without R use last day.
  const getMostRecentBillingCycleEnd = (referenceDate, renewalDay) => {
    if (renewalDay == null || renewalDay < 1 || renewalDay > 31) return null;
    const t = new Date(referenceDate);
    const y = t.getFullYear();
    const m = t.getMonth();
    const d = t.getDate();
    const lastDayThisMonth = getLastDayOfMonth(y, m + 1);
    const renewalDayThisMonth = Math.min(renewalDay, lastDayThisMonth);
    let renewalDate = new Date(y, m, renewalDayThisMonth);
    if (renewalDate > t) {
      const prevM = m === 0 ? 11 : m - 1;
      const prevY = m === 0 ? y - 1 : y;
      const lastDayPrev = getLastDayOfMonth(prevY, prevM + 1);
      const renewalDayPrev = Math.min(renewalDay, lastDayPrev);
      renewalDate = new Date(prevY, prevM, renewalDayPrev);
    }
    const cycleEnd = new Date(renewalDate);
    cycleEnd.setDate(cycleEnd.getDate() - 1);
    return cycleEnd;
  };

  const toDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Billable-days metric: 30-day window ending on cycle end; value = count of days in that window with zero check-ins.
  const billableDaysMetric = (() => {
    if (!userData) return null;
    const renewalDay = getRenewalDay(userData.stripeDetails?.createdAt);
    if (renewalDay == null) return { label: null, value: null, windowStart: null, windowEnd: null };
    const cycleEnd = getMostRecentBillingCycleEnd(new Date(), renewalDay);
    if (!cycleEnd) return { label: null, value: null, windowStart: null, windowEnd: null };
    const windowEnd = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth(), cycleEnd.getDate());
    const windowStart = new Date(windowEnd);
    windowStart.setDate(windowStart.getDate() - 29);
    const daysWithCheckIn = new Set();
    (userData.journalEntries || []).forEach((entry) => {
      const ts = entry.timestamp;
      const date = ts instanceof Date ? ts : (ts != null ? new Date(ts) : null);
      if (date && !isNaN(date.getTime())) {
        daysWithCheckIn.add(toDateString(date));
      }
    });
    const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
    const missingDates = [];
    for (let cur = new Date(windowStart); cur <= windowEnd; cur.setDate(cur.getDate() + 1)) {
      if (!daysWithCheckIn.has(toDateString(cur))) {
        missingDates.push(fmt(new Date(cur)));
      }
    }
    return {
      label: `Billable Days ${fmt(windowStart)} to ${fmt(windowEnd)}`,
      value: missingDates.length,
      missingDates,
      windowStart,
      windowEnd,
    };
  })();

  // Enhanced Optimal Pace Prediction System
  const calculateEnhancedOptimalPace = (entries, targetIndex) => {
    if (!entries || entries.length === 0) return null;
    
    const targetEntry = entries[targetIndex];
    if (!targetEntry || !targetEntry.emotionalStates || !targetEntry.hoursWorked) return null;
    
    try {
      // Get historical entries up to the target index
      const historicalEntries = entries.slice(0, targetIndex + 1);
      
      // Calculate time-weighted importance (exponential decay)
      const currentTime = new Date(targetEntry.timestamp);
      const lambda = 0.1; // Decay rate (adjustable)
      
      const weightedEntries = historicalEntries.map((entry, index) => {
        const entryTime = new Date(entry.timestamp);
        const daysDiff = (currentTime - entryTime) / (1000 * 60 * 60 * 24);
        const weight = Math.exp(-lambda * daysDiff);
        
        return {
          ...entry,
          weight,
          daysAgo: daysDiff
        };
      }).filter(entry => entry.weight > 0.01); // Only include entries with meaningful weight
      
      if (weightedEntries.length === 0) return null;
      
      // Calculate weighted averages for emotional factors
      const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
      const weightedEmotionalAverages = {};
      
      emotionalFactors.forEach(factor => {
        const validEntries = weightedEntries.filter(entry => 
          entry.emotionalStates && 
          entry.emotionalStates[factor] !== null && 
          entry.emotionalStates[factor] !== undefined
        );
        
        if (validEntries.length > 0) {
          const weightedSum = validEntries.reduce((sum, entry) => 
            sum + (entry.emotionalStates[factor] * entry.weight), 0
          );
          const totalWeight = validEntries.reduce((sum, entry) => sum + entry.weight, 0);
          weightedEmotionalAverages[factor] = weightedSum / totalWeight;
        }
      });
      
      // Calculate weighted pressure average
      const pressureEntries = weightedEntries.filter(entry => 
        entry.pressureScore !== null && entry.pressureScore !== undefined
      );
      
      let weightedPressureAverage = 5; // Default
      if (pressureEntries.length > 0) {
        const weightedPressureSum = pressureEntries.reduce((sum, entry) => 
          sum + (entry.pressureScore * entry.weight), 0
        );
        const totalPressureWeight = pressureEntries.reduce((sum, entry) => sum + entry.weight, 0);
        weightedPressureAverage = weightedPressureSum / totalPressureWeight;
      }
      
      // Calculate weighted hours worked average
      const hoursEntries = weightedEntries.filter(entry => 
        entry.hoursWorked !== null && entry.hoursWorked !== undefined
      );
      
      let weightedHoursAverage = 8; // Default
      if (hoursEntries.length > 0) {
        const weightedHoursSum = hoursEntries.reduce((sum, entry) => 
          sum + (entry.hoursWorked * entry.weight), 0
        );
        const totalHoursWeight = hoursEntries.reduce((sum, entry) => sum + entry.weight, 0);
        weightedHoursAverage = weightedHoursSum / totalHoursWeight;
      }
      
      // Calculate current emotional arousal (inverted scale)
      const currentEmotionalValues = emotionalFactors
        .map(factor => weightedEmotionalAverages[factor])
        .filter(val => val !== undefined)
        .map(val => 10 - val); // Invert for arousal calculation
      
      const currentArousal = currentEmotionalValues.length > 0 
        ? currentEmotionalValues.reduce((sum, val) => sum + val, 0) / currentEmotionalValues.length
        : 5;
      
      // Determine task complexity based on ACTUAL pressure score for this day
      const actualPressure = targetEntry.pressureScore;
      const isHighComplexity = actualPressure >= 6;
      
      // Calculate optimal arousal range based on actual pressure
      let optimalArousalRange;
      if (isHighComplexity) {
        optimalArousalRange = { min: 1.0, max: 3.0 };
      } else {
        optimalArousalRange = { min: 1.0, max: 5.0 };
      }
      
      // Calculate the middle of the optimal range as the target arousal
      const targetArousal = (optimalArousalRange.min + optimalArousalRange.max) / 2;
      
      // Calculate arousal ratio to move toward the middle of the optimal range
      const arousalRatio = targetArousal / Math.max(currentArousal, 0.1);
      
      // Apply pressure adjustment factor
      const pressureAdjustmentFactor = 1 + (weightedPressureAverage - 5) * 0.1; // ±10% adjustment based on pressure
      
      // Calculate optimal hours with pressure adjustment
      let optimalHours = weightedHoursAverage * arousalRatio * pressureAdjustmentFactor;
      
      // Apply caps
      const cappedArousalRatio = Math.max(0.5, Math.min(2.0, arousalRatio));
      optimalHours = Math.max(2, Math.min(18, optimalHours));
      
      // Calculate confidence based on data quality
      const dataQualityScore = Math.min(1, weightedEntries.length / 10); // More entries = higher confidence
      const pressureVariationScore = pressureEntries.length > 1 ? 
        Math.min(1, calculateStandardDeviation(pressureEntries.map(e => e.pressureScore)) / 3) : 0.5;
      const emotionalVariationScore = currentEmotionalValues.length > 0 ?
        Math.min(1, calculateStandardDeviation(currentEmotionalValues) / 2) : 0.5;
      
      const confidenceScore = (dataQualityScore * 0.4 + pressureVariationScore * 0.3 + emotionalVariationScore * 0.3);
      
      // Calculate prediction interval
      const predictionInterval = calculatePredictionInterval(optimalHours, confidenceScore, weightedEntries.length);
      
      // Determine confidence level
      let confidenceLevel;
      if (confidenceScore >= 0.8) confidenceLevel = 'Very High';
      else if (confidenceScore >= 0.6) confidenceLevel = 'High';
      else if (confidenceScore >= 0.4) confidenceLevel = 'Moderate';
      else if (confidenceScore >= 0.2) confidenceLevel = 'Low';
      else confidenceLevel = 'Very Low';
      
      // Generate explanation
      const explanation = generatePredictionExplanation(
        optimalHours,
        confidenceLevel,
        weightedEntries.length,
        weightedPressureAverage,
        currentArousal,
        optimalArousalRange,
        pressureAdjustmentFactor
      );
      
      return {
        optimalHours: Math.round(optimalHours * 10) / 10,
        confidence: {
          score: Math.round(confidenceScore * 100) / 100,
          level: confidenceLevel,
          explanation: explanation
        },
        predictionInterval,
        metadata: {
          entriesUsed: weightedEntries.length,
          weightedPressure: Math.round(weightedPressureAverage * 10) / 10,
          actualPressure: actualPressure,
          currentArousal: Math.round(currentArousal * 10) / 10,
          targetArousal: Math.round(targetArousal * 10) / 10,
          arousalRatio: Math.round(cappedArousalRatio * 100) / 100,
          pressureAdjustment: Math.round(pressureAdjustmentFactor * 100) / 100,
          optimalRange: optimalArousalRange
        }
      };
      
    } catch (error) {
      console.error('Error calculating enhanced optimal pace:', error);
      return null;
    }
  };

  // Same date series as Optimal Pace chart (entry + work dates per check-in)
  const buildOptimalPaceOnlySeries = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter((entry) => entry.timestamp && entry.hoursWorked)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (entries.length === 0) return null;

    const dateMap = new Map();
    entries.forEach((entry, index) => {
      const entryDate = new Date(entry.timestamp);
      const entryDateStr = entryDate.toLocaleDateString();
      const workDate = new Date(entryDate);
      workDate.setDate(workDate.getDate() - 1);
      const workDateStr = workDate.toLocaleDateString();
      if (!dateMap.has(workDateStr)) {
        dateMap.set(workDateStr, { enhancedOptimalPace: null, prediction: null });
      }
      if (!dateMap.has(entryDateStr)) {
        dateMap.set(entryDateStr, { enhancedOptimalPace: null, prediction: null });
      }
      const prediction = calculateEnhancedOptimalPace(entries, index);
      dateMap.get(entryDateStr).enhancedOptimalPace = prediction ? prediction.optimalHours : null;
      dateMap.get(entryDateStr).prediction = prediction;
    });

    const sortedDates = Array.from(dateMap.keys()).sort((a, b) => new Date(a) - new Date(b));
    return { dateMap, sortedDates };
  };

  const getTimestampMs = (timestamp) => {
    const ms = entryTimestampToMillis(timestamp);
    return Number.isFinite(ms) ? ms : null;
  };

  const mergedEntriesChronological = (userData?.journalEntries || [])
    .filter((entry) => getTimestampMs(entry.timestamp) != null)
    .sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp));

  const latestMergedEntry = mergedEntriesChronological.length > 0
    ? mergedEntriesChronological[mergedEntriesChronological.length - 1]
    : null;
  const earliestMergedEntry = mergedEntriesChronological.length > 0
    ? mergedEntriesChronological[0]
    : null;

  const startDateSource =
    userData?.lookupSource === 'founders-first'
      ? (userData?.startTime || earliestMergedEntry?.timestamp || null)
      : (earliestMergedEntry?.timestamp || userData?.startTime || null);
  const lastActiveSource = latestMergedEntry?.timestamp || userData?.lastActive || null;
  const startDate = convertFirebaseTimestamp(startDateSource);
  const lastActiveDate = convertFirebaseTimestamp(lastActiveSource);
  const startDateDisplay = startDate ? startDate.toLocaleDateString() : 'Unknown';
  const lastActiveDisplay = lastActiveDate ? lastActiveDate.toLocaleDateString() : 'Unknown';

  // Helper function to calculate standard deviation
  const calculateStandardDeviation = (values) => {
    if (values.length === 0) return 0;
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  };

  // Helper function to calculate prediction interval
  const calculatePredictionInterval = (optimalHours, confidenceScore, sampleSize) => {
    // Calculate margin of error based on confidence and sample size
    const baseMargin = 2.0; // Base margin in hours
    const confidenceMultiplier = 1 - confidenceScore; // Lower confidence = wider interval
    const sampleSizeMultiplier = Math.max(0.5, Math.min(1.5, sampleSize / 5)); // More samples = narrower interval
    
    const margin = baseMargin * confidenceMultiplier * sampleSizeMultiplier;
    
    return {
      min: Math.max(0, Math.round((optimalHours - margin) * 10) / 10),
      max: Math.min(18, Math.round((optimalHours + margin) * 10) / 10),
      margin: Math.round(margin * 10) / 10
    };
  };

  // Helper function to generate prediction explanation
  const generatePredictionExplanation = (
    optimalHours,
    confidenceLevel,
    entriesUsed,
    weightedPressure,
    currentArousal,
    optimalArousalRange,
    pressureAdjustment
  ) => {
    const explanations = [];
    
    // Data quality explanation
    if (entriesUsed >= 10) {
      explanations.push(`Based on ${entriesUsed} historical entries with strong statistical foundation`);
    } else if (entriesUsed >= 5) {
      explanations.push(`Based on ${entriesUsed} historical entries with moderate statistical foundation`);
    } else {
      explanations.push(`Based on ${entriesUsed} historical entries with limited statistical foundation`);
    }
    
    // Pressure explanation
    if (weightedPressure >= 7) {
      explanations.push(`High pressure environment (${weightedPressure.toFixed(1)}) suggests conservative workload`);
    } else if (weightedPressure <= 3) {
      explanations.push(`Low pressure environment (${weightedPressure.toFixed(1)}) allows for increased workload`);
    } else {
      explanations.push(`Moderate pressure environment (${weightedPressure.toFixed(1)}) provides balanced workload guidance`);
    }
    
    // Arousal explanation
    if (currentArousal > optimalArousalRange.max) {
      explanations.push(`Current arousal (${currentArousal.toFixed(1)}) above optimal zone suggests reducing workload`);
    } else if (currentArousal < optimalArousalRange.min) {
      explanations.push(`Current arousal (${currentArousal.toFixed(1)}) below optimal zone suggests increasing workload`);
    } else {
      explanations.push(`Current arousal (${currentArousal.toFixed(1)}) within optimal zone suggests maintaining current pace`);
    }
    
    // Confidence explanation
    explanations.push(`${confidenceLevel} confidence due to data quality and variation`);
    
    return explanations.join('. ');
  };

  const calculateTotalEmotionScore = (emotionalStates) => {
    if (!emotionalStates) return 0;
    const factors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
    const scores = factors.map(factor => emotionalStates[factor] || 0);
    return scores.reduce((sum, score) => sum + score, 0);
  };

  const calculateAverageEmotionScore = (emotionalStates) => {
    if (!emotionalStates) return 0;
    const factors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
    const scores = factors.map(factor => emotionalStates[factor] || 0);
    const validScores = scores.filter(score => score > 0);
    return validScores.length > 0 ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length : 0;
  };

  // Weekly Hours Worked: each point = estimated hours for last 7 days from check-ins in the lookup window.
  // If window has 7 check-ins, use sum of hours; otherwise (sum / count) * 7.
  // Hours are attributed to work date (day before entry date). One point per check-in.
  const prepareHoursWorkedChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp && entry.hoursWorked != null)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (entries.length === 0) return null;

    const toWorkDate = (timestamp) => {
      const d = new Date(timestamp);
      d.setDate(d.getDate() - 1);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const labels = entries.map(entry => new Date(entry.timestamp).toLocaleDateString());
    const data = entries.map((entry, i) => {
      const workDateMs = toWorkDate(entry.timestamp);
      const windowStartMs = workDateMs - 6 * 24 * 60 * 60 * 1000;
      let sum = 0;
      let count = 0;
      for (let j = 0; j < entries.length; j++) {
        const w = toWorkDate(entries[j].timestamp);
        if (w >= windowStartMs && w <= workDateMs) {
          sum += entries[j].hoursWorked ?? 0;
          count += 1;
        }
      }
      const estimatedWeekly = count === 0 ? 0 : (sum / count) * 7;
      return Math.round(estimatedWeekly * 10) / 10;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Weekly Hours Worked',
          data,
          borderColor: theme.colors.doryDarkerBlue,
          backgroundColor: theme.colors.doryDarkerBlue + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
        }
      ]
    };
  };

  // Combined Weekly Hours Worked + Capacity: estimated weekly hours from (sum/count)*7 in lookup window
  const prepareWeeklyHoursAndCapacityChartData = () => {
    if (!userData?.journalEntries) return null;

    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
      return new Date(timestamp).getTime() || 0;
    };

    const sortedEntries = [...userData.journalEntries].sort((a, b) => {
      const timeA = getTimestampValue(a.timestamp);
      const timeB = getTimestampValue(b.timestamp);
      return timeA - timeB;
    });

    const entries = sortedEntries.filter(entry => entry.hoursWorked != null);
    if (entries.length === 0) return null;

    const toWorkDate = (timestamp) => {
      const d = new Date(getTimestampValue(timestamp));
      d.setDate(d.getDate() - 1);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const labels = entries.map(entry => new Date(entry.timestamp).toLocaleDateString());

    const weeklyHoursData = entries.map((entry, i) => {
      const workDateMs = toWorkDate(entry.timestamp);
      const windowStartMs = workDateMs - 6 * 24 * 60 * 60 * 1000;
      let sum = 0;
      let count = 0;
      for (let j = 0; j < entries.length; j++) {
        const w = toWorkDate(entries[j].timestamp);
        if (w >= windowStartMs && w <= workDateMs) {
          sum += entries[j].hoursWorked ?? 0;
          count += 1;
        }
      }
      const estimatedWeekly = count === 0 ? 0 : (sum / count) * 7;
      return Math.round(estimatedWeekly * 10) / 10;
    });

    const capacityData = entries.map((entry) => {
      const entryIndex = sortedEntries.findIndex(e =>
        getTimestampValue(e.timestamp) === getTimestampValue(entry.timestamp)
      );
      if (entryIndex === -1) return null;
      const windowStart = Math.max(0, entryIndex - 6);
      const windowEntries = sortedEntries
        .slice(windowStart, entryIndex + 1)
        .filter(e => e.hoursWorked != null);
      const last7 = windowEntries.slice(-7);
      const optimalPaces = [];
      last7.forEach((e) => {
        const idx = sortedEntries.findIndex(x => getTimestampValue(x.timestamp) === getTimestampValue(e.timestamp));
        if (idx !== -1) {
          const paceResult = calculateEnhancedOptimalPace(sortedEntries, idx);
          if (paceResult?.optimalHours != null) optimalPaces.push(paceResult.optimalHours);
        }
      });
      if (optimalPaces.length === 0) return null;
      return Math.round(optimalPaces.reduce((sum, p) => sum + p, 0));
    });

    return {
      labels,
      datasets: [
        {
          label: 'Weekly Hours Worked',
          data: weeklyHoursData,
          borderColor: theme.colors.doryDarkerBlue,
          backgroundColor: theme.colors.doryDarkerBlue + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: 'Capacity',
          data: capacityData,
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primary + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          borderDash: [5, 5],
          pointStyle: 'triangle',
        }
      ]
    };
  };

  // Optimal Pace only (single series) — same dates as Optimal Pace vs Hours Worked
  const prepareOptimalPaceOnlyChartData = () => {
    const series = buildOptimalPaceOnlySeries();
    if (!series) return null;

    const { dateMap, sortedDates } = series;

    const confidenceToColor = (level) => {
      if (!level) return theme.colors.accent;
      const L = level.toLowerCase();
      if (L === 'very high' || L === 'high') return '#22c55e';
      if (L === 'moderate') return '#eab308';
      return '#ef4444';
    };

    const optimalPaceData = sortedDates.map(date => {
      const v = dateMap.get(date).enhancedOptimalPace;
      return v != null ? Math.round(v) : null;
    });
    const pointColors = sortedDates.map(date => {
      const pred = dateMap.get(date).prediction;
      return pred?.confidence?.level ? confidenceToColor(pred.confidence.level) : theme.colors.accent;
    });
    const pointConfidenceLabels = sortedDates.map(date => {
      const pred = dateMap.get(date).prediction;
      const level = pred?.confidence?.level;
      if (!level) return null;
      return level + ' Confidence';
    });

    return {
      labels: sortedDates,
      datasets: [
        {
          label: 'Optimal Pace',
          data: optimalPaceData,
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.accent + '20',
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointConfidenceLabels,
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          borderDash: [5, 5],
          pointStyle: 'triangle'
        }
      ]
    };
  };

  const prepareOptimalPaceChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp && entry.hoursWorked)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Create a map of all unique dates (both work dates and entry dates)
    const dateMap = new Map();
    
    entries.forEach((entry, index) => {
      const entryDate = new Date(entry.timestamp);
      const entryDateStr = entryDate.toLocaleDateString();
      
      // Work date is one day before the entry date
      const workDate = new Date(entryDate);
      workDate.setDate(workDate.getDate() - 1);
      const workDateStr = workDate.toLocaleDateString();
      
      // Initialize dates in the map if they don't exist
      if (!dateMap.has(workDateStr)) {
        dateMap.set(workDateStr, { hoursWorked: null, enhancedOptimalPace: null, prediction: null });
      }
      if (!dateMap.has(entryDateStr)) {
        dateMap.set(entryDateStr, { hoursWorked: null, enhancedOptimalPace: null, prediction: null });
      }
      
      // Hours worked goes on the work date (day before entry)
      const dateData = dateMap.get(workDateStr);
      dateData.hoursWorked = entry.hoursWorked || 0;
      
      // Enhanced optimal pace goes on the entry date
      const entryData = dateMap.get(entryDateStr);
      const prediction = calculateEnhancedOptimalPace(entries, index);
      entryData.enhancedOptimalPace = prediction ? prediction.optimalHours : null;
      entryData.prediction = prediction;
    });
    
    // Sort dates chronologically
    const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
      return new Date(a) - new Date(b);
    });
    
    // Build arrays for chart (Optimal Pace rounded to nearest whole number)
    const labels = sortedDates;
    const hoursWorkedData = sortedDates.map(date => dateMap.get(date).hoursWorked);
    const enhancedOptimalPaceData = sortedDates.map(date => {
      const v = dateMap.get(date).enhancedOptimalPace;
      return v != null ? Math.round(v) : null;
    });
    const enhancedPredictions = sortedDates.map(date => dateMap.get(date).prediction);

    return {
      labels,
      datasets: [
        {
          label: 'Hours Worked',
          data: hoursWorkedData,
          borderColor: theme.colors.doryDarkerBlue,
          backgroundColor: theme.colors.doryDarkerBlue + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          borderDash: [] // Solid line
        },
        {
          label: 'Optimal Pace',
          data: enhancedOptimalPaceData,
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.accent + '20',
          pointRadius: 8,
          pointHoverRadius: 10,
          tension: 0.1,
          borderDash: [5, 5], // Dashed line
          pointStyle: 'triangle'
        }
      ],
      enhancedPredictions // Store for custom tooltip
    };
  };

  // Performance Debt: cumulative sum of (hours worked − optimal pace) per day.
  // Aligned with Sprint Recovery Protocol: use FULL journal entries (not filtered) for optimal pace
  // so historical context and indices match. For each work date D: optimal = entry on D, hours = entry on D+1.
  const preparePerformanceDebtChartData = () => {
    if (!userData?.journalEntries) return null;

    const toDateKey = (timestamp) => {
      const d = convertFirebaseTimestamp(timestamp);
      if (!d || Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d.toISOString().split('T')[0];
    };

    const allEntries = [...userData.journalEntries]
      .filter((entry) => toDateKey(entry.timestamp) != null)
      .sort((a, b) => entryTimestampToMillis(a.timestamp) - entryTimestampToMillis(b.timestamp));
    if (allEntries.length === 0) return null;

    // Step 1: Optimal pace by entry date — same as Sprint Recovery: all entries, default 8 when null
    const optimalPaceByDate = new Map();
    allEntries.forEach((entry, index) => {
      const dateKey = toDateKey(entry.timestamp);
      if (!dateKey) return;
      const optimalHours = calculateEnhancedOptimalPace(allEntries, index)?.optimalHours ?? 8;
      optimalPaceByDate.set(dateKey, optimalHours);
    });

    // Step 2: Hours worked by work date (entry on date E reports hours for date E-1)
    const hoursWorkedByDate = new Map();
    allEntries.forEach(entry => {
      if (entry.hoursWorked == null) return;
      const entryDate = convertFirebaseTimestamp(entry.timestamp);
      if (!entryDate || Number.isNaN(entryDate.getTime())) return;
      entryDate.setHours(0, 0, 0, 0);
      const workDate = new Date(entryDate);
      workDate.setDate(workDate.getDate() - 1);
      const dateKey = workDate.toISOString().split('T')[0];
      hoursWorkedByDate.set(dateKey, entry.hoursWorked);
    });

    // Step 3: For each work date D that has both optimal (from entry D) and hours (from entry D+1), compute diff
    const workDatesWithBoth = [...hoursWorkedByDate.keys()].filter(dateKey =>
      optimalPaceByDate.has(dateKey)
    ).sort();

    // Same as Sprint Recovery: cumulative deficit never goes below zero (remaining deficit)
    let runningSum = 0;
    const labels = workDatesWithBoth.map(dateKey => {
      const [y, m, d] = dateKey.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString();
    });
    const debtData = workDatesWithBoth.map(dateKey => {
      const optimalHours = optimalPaceByDate.get(dateKey) ?? 0;
      const hoursWorked = hoursWorkedByDate.get(dateKey) ?? 0;
      const diff = hoursWorked - optimalHours;
      runningSum = Math.max(0, runningSum + diff);
      return Math.round(runningSum * 10) / 10;
    });

    if (labels.length === 0) return null;

    return {
      labels,
      datasets: [
        {
          label: 'Performance Debt',
          data: debtData,
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primary + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          fill: true,
        }
      ]
    };
  };

  const prepareArousalChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp && entry.hoursWorked)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const labels = entries.map(entry => 
      new Date(entry.timestamp).toLocaleDateString()
    );
    
    const optimalPaceData = entries.map(entry => 
      calculateOptimalPaceForEntry(entry)
    );

    // Calculate enhanced optimal pace predictions for each entry
    const enhancedOptimalPaceData = entries.map((entry, index) => {
      const prediction = calculateEnhancedOptimalPace(entries, index);
      return prediction ? prediction.optimalHours : null;
    });

    // Store enhanced predictions for tooltip display
    const enhancedPredictions = entries.map((entry, index) => {
      const prediction = calculateEnhancedOptimalPace(entries, index);
      return prediction;
    });

    // Calculate arousal data for each entry
    const arousalData = entries.map((entry, index) => {
      const prediction = calculateEnhancedOptimalPace(entries, index);
      if (!prediction) return null;
      
      // Calculate Mental Reserves directly from this day's emotional factors (not weighted)
      const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
      const currentDayEmotionalValues = emotionalFactors
        .map(factor => entry.emotionalStates?.[factor])
        .filter(val => val !== null && val !== undefined && val >= 0 && val <= 10);
      
      // Calculate average emotional score for this day (not inverted - this is the raw emotional score)
      const currentDayEmotionalAverage = currentDayEmotionalValues.length > 0 
        ? currentDayEmotionalValues.reduce((sum, val) => sum + val, 0) / currentDayEmotionalValues.length
        : 5; // Fallback if no emotional data
      
      // Convert to arousal (inverted) and then to mental reserves (inverted again = back to emotional score)
      const arousal = 10 - currentDayEmotionalAverage; // Convert emotional score to arousal
      const mentalReserves = currentDayEmotionalAverage; // Mental reserves = emotional score directly
      
      const actualPressure = entry.pressureScore;
      const isHighComplexity = actualPressure >= 6;
      const optimalRange = isHighComplexity ? 
        { min: 1.0, max: 3.0 } : { min: 1.0, max: 5.0 };
      
      // Calculate target mental reserves (inverted from target arousal)
      const targetMentalReserves = 10 - prediction.metadata.targetArousal;
      
      return {
        arousal: arousal,
        mentalReserves: mentalReserves,
        optimalRange: optimalRange,
        actualPressure: actualPressure,
        targetArousal: prediction.metadata.targetArousal,
        targetMentalReserves: targetMentalReserves
      };
    });

    // Calculate optimal zone min/max lines (inverted by subtracting from 10)
    const optimalZoneMinData = arousalData.map(item => item ? 10 - item.optimalRange.max : null);
    const optimalZoneMaxData = arousalData.map(item => item ? 10 - item.optimalRange.min : null);
    const mentalReservesData = arousalData.map(item => item ? item.mentalReserves : null);
    const targetMentalReservesData = arousalData.map(item => item ? item.targetMentalReserves : null);

    // Calculate arousal zone status for dots at y=0
    const arousalZoneData = arousalData.map((data) => {
      if (!data) return null;
      
      let zoneStatus;
      let color;
      if (data.arousal >= data.optimalRange.min && data.arousal <= data.optimalRange.max) {
        zoneStatus = 'optimal';
        color = '#22c55e'; // Green
      } else if (data.arousal < data.optimalRange.min) {
        zoneStatus = 'under';
        color = '#eab308'; // Yellow
      } else {
        zoneStatus = 'over';
        color = '#ef4444'; // Red
      }
      
      return {
        arousal: data.arousal,
        zoneStatus: zoneStatus,
        color: color,
        optimalRange: data.optimalRange,
        actualPressure: data.actualPressure
      };
    });

    // Create arousal zone data points at y=0
    const arousalZonePoints = arousalZoneData.map((zoneData) => {
      return zoneData ? 0 : null;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Mental Reserves',
          data: mentalReservesData,
          borderColor: '#8b5cf6', // Purple
          backgroundColor: '#8b5cf6' + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          borderDash: [] // Solid line
        },
        {
          label: 'Optimal Zone Target',
          data: targetMentalReservesData,
          borderColor: '#06b6d4', // Cyan
          backgroundColor: '#06b6d4' + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1,
          borderDash: [3, 3], // Dotted line
          pointStyle: 'diamond'
        },
        {
          label: 'Optimal Zone Min',
          data: optimalZoneMinData,
          borderColor: '#10b981', // Green
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          borderDash: [2, 2], // Dashed line
          showLine: true
        },
        {
          label: 'Optimal Zone Max',
          data: optimalZoneMaxData,
          borderColor: '#f59e0b', // Orange
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          borderDash: [2, 2], // Dashed line
          showLine: true
        },
      ],
      arousalZoneData, // Store for custom tooltip
      arousalData // Store arousal data for tooltip
    };
  };

  const prepareHoursPressureChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const labels = entries.map(entry => 
      new Date(entry.timestamp).toLocaleDateString()
    );

    return {
      labels,
      datasets: [
        {
          label: 'Pressure Score',
          data: entries.map(entry => entry.pressureScore || 0),
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.accent + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1
        }
      ]
    };
  };

  const prepareEmotionScoreChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp && entry.emotionalStates)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const labels = entries.map(entry => 
      new Date(entry.timestamp).toLocaleDateString()
    );

    // Define emotional factors with their colors and user-facing names
    const emotionalFactors = [
      { key: 'focus', label: 'Focus', color: '#3b82f6' }, // Blue
      { key: 'calm', label: 'Calm', color: '#10b981' }, // Green
      { key: 'happiness', label: 'Positivity', color: '#f59e0b' }, // Amber
      { key: 'depression', label: 'Satisfaction', color: '#8b5cf6' }, // Purple
      { key: 'mania', label: 'Control', color: '#ef4444' }, // Red
      { key: 'energy', label: 'Energy', color: '#06b6d4' }, // Cyan
      { key: 'hours', label: 'Personal Identity', color: '#84cc16' } // Lime
    ];

    // Create datasets for each emotional factor
    const datasets = emotionalFactors.map(factor => ({
      label: factor.label,
      data: entries.map(entry => {
        const value = entry.emotionalStates?.[factor.key];
        return value !== null && value !== undefined ? parseFloat(value) : 0;
      }),
      backgroundColor: factor.color,
      borderColor: factor.color,
      borderWidth: 1
    }));

    return {
      labels,
      datasets
    };
  };

  const prepareRegressionChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.pressureScore !== null && entry.emotionalStates)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (entries.length < 2) return null;

    const dataPoints = entries.map(entry => ({
      x: entry.pressureScore,
      y: calculateAverageEmotionScore(entry.emotionalStates)
    }));

    // Calculate linear regression for best fit line
    const n = dataPoints.length;
    const sumX = dataPoints.reduce((sum, point) => sum + point.x, 0);
    const sumY = dataPoints.reduce((sum, point) => sum + point.y, 0);
    const sumXY = dataPoints.reduce((sum, point) => sum + (point.x * point.y), 0);
    const sumXX = dataPoints.reduce((sum, point) => sum + (point.x * point.x), 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Create trend line points
    const trendLinePoints = [
      { x: 0, y: intercept },
      { x: 10, y: slope * 10 + intercept }
    ];

    return {
      datasets: [
        {
          label: 'Data Points',
          data: dataPoints,
          backgroundColor: theme.colors.primary + '80',
          borderColor: theme.colors.primary,
          pointRadius: 8,
          pointHoverRadius: 10,
          showLine: false
        },
        {
          label: 'Best Fit Line',
          data: trendLinePoints,
          borderColor: theme.colors.accent,
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          showLine: true,
          tension: 0
        }
      ]
    };
  };

  const prepareMentalResilienceChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const labels = entries.map(entry => 
      new Date(entry.timestamp).toLocaleDateString()
    );

    const resilienceScores = entries.map((entry, index) => 
      calculateMentalResilienceForEntry(entries, index)
    );

    return {
      labels,
      datasets: [
        {
          label: 'Mental Resilience Score',
          data: resilienceScores,
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.accent + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1
        }
      ]
    };
  };

  const prepareRefreshRateChartData = () => {
    if (!userData?.journalEntries) return null;

    const entries = userData.journalEntries
      .filter(entry => entry.timestamp && entry.emotionalStates)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (entries.length === 0) return null;

    const labels = entries.map(entry => 
      new Date(entry.timestamp).toLocaleDateString()
    );

    // Calculate rolling Refresh Rate for each entry using the same lookback window as Mental Resilience
    const refreshRates = entries.map((entry, index) => {
      // Use up to 15 previous entries for calculation (same as Mental Resilience)
      const startIndex = Math.max(0, index - 14);
      const relevantEntries = entries.slice(startIndex, index + 1);
      
      // Calculate total Mental Reserves sum for each entry in the window
      const totalReservesSums = relevantEntries
        .map(entry => calculateTotalEmotionScore(entry.emotionalStates))
        .filter(sum => sum > 0); // Filter out entries with no valid data
      
      if (totalReservesSums.length === 0) return null;
      
      // Calculate average of recent total Mental Reserves sums
      const averageTotalReserves = totalReservesSums.reduce((sum, val) => sum + val, 0) / totalReservesSums.length;
      
      // Convert to percentage on scale from 0 to 70
      const refreshRate = (averageTotalReserves / 70) * 100;
      
      return Math.round(refreshRate * 10) / 10; // Round to 1 decimal place
    });

    return {
      labels,
      datasets: [
        {
          label: 'Refresh Rate (%)',
          data: refreshRates,
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primary + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1
        }
      ]
    };
  };

  // Capacity: same as Optimal Pace Solo — sum of last 7 Enhanced Optimal Paces, computed per day
  const prepareCapacityChartData = () => {
    if (!userData?.journalEntries) return null;

    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
      return new Date(timestamp).getTime() || 0;
    };

    const sortedEntries = [...userData.journalEntries].sort((a, b) => {
      const timeA = getTimestampValue(a.timestamp);
      const timeB = getTimestampValue(b.timestamp);
      return timeA - timeB;
    });

    const entriesWithHours = sortedEntries
      .filter(entry => entry.hoursWorked !== null && entry.hoursWorked !== undefined);

    if (entriesWithHours.length === 0) return null;

    const labels = entriesWithHours.map(entry => new Date(entry.timestamp).toLocaleDateString());
    const capacityScores = entriesWithHours.map((entry) => {
      const entryIndex = sortedEntries.findIndex(e =>
        getTimestampValue(e.timestamp) === getTimestampValue(entry.timestamp)
      );
      if (entryIndex === -1) return null;
      const windowStart = Math.max(0, entryIndex - 6);
      const windowEntries = sortedEntries
        .slice(windowStart, entryIndex + 1)
        .filter(e => e.hoursWorked != null);
      const last7 = windowEntries.slice(-7);
      const optimalPaces = [];
      last7.forEach((e) => {
        const idx = sortedEntries.findIndex(x => getTimestampValue(x.timestamp) === getTimestampValue(e.timestamp));
        if (idx !== -1) {
          const paceResult = calculateEnhancedOptimalPace(sortedEntries, idx);
          if (paceResult?.optimalHours != null) optimalPaces.push(paceResult.optimalHours);
        }
      });
      if (optimalPaces.length === 0) return null;
      return Math.round(optimalPaces.reduce((sum, p) => sum + p, 0));
    });

    return {
      labels,
      datasets: [
        {
          label: 'Capacity',
          data: capacityScores,
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primary + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1
        }
      ]
    };
  };

  // Performance: same as Optimal Pace Solo — (10 - |mentalReserves - 8|) * avg(last 5 Optimal Paces), per day
  const preparePerformanceChartData = () => {
    if (!userData?.journalEntries) return null;

    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
      return new Date(timestamp).getTime() || 0;
    };

    const sortedEntries = [...userData.journalEntries].sort((a, b) => {
      const timeA = getTimestampValue(a.timestamp);
      const timeB = getTimestampValue(b.timestamp);
      return timeA - timeB;
    });

    const entriesWithHours = sortedEntries
      .filter(entry => (entry.hoursWorked != null) && entry.emotionalStates);

    if (entriesWithHours.length === 0) return null;

    const optimalTarget = 8;
    const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];

    const labels = entriesWithHours.map(entry => new Date(entry.timestamp).toLocaleDateString());
    const performanceScores = entriesWithHours.map((entry) => {
      const entryIndex = sortedEntries.findIndex(e =>
        getTimestampValue(e.timestamp) === getTimestampValue(entry.timestamp)
      );
      if (entryIndex === -1) return null;

      const factorScores = emotionalFactors
        .map(f => entry.emotionalStates?.[f])
        .filter(val => val != null && val >= 0 && val <= 10);
      if (factorScores.length === 0) return null;
      const currentMentalReserves = factorScores.reduce((s, v) => s + v, 0) / factorScores.length;

      const windowStart = Math.max(0, entryIndex - 4);
      const windowEntries = sortedEntries
        .slice(windowStart, entryIndex + 1)
        .filter(e => e.hoursWorked != null);
      const last5 = windowEntries.slice(-5);
      const optimalPaces = [];
      last5.forEach((e) => {
        const idx = sortedEntries.findIndex(x => getTimestampValue(x.timestamp) === getTimestampValue(e.timestamp));
        if (idx !== -1) {
          const paceResult = calculateEnhancedOptimalPace(sortedEntries, idx);
          if (paceResult?.optimalHours != null) optimalPaces.push(paceResult.optimalHours);
        }
      });
      if (optimalPaces.length === 0) return null;
      const averageOptimalPace = optimalPaces.reduce((s, p) => s + p, 0) / optimalPaces.length;
      const difference = Math.abs(currentMentalReserves - optimalTarget);
      const score = (10 - difference) * averageOptimalPace;
      return Math.round(score);
    });

    return {
      labels,
      datasets: [
        {
          label: 'Performance',
          data: performanceScores,
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.accent + '20',
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.1
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 24,
        title: {
          display: true,
          text: 'Hours'
        }
      },
    },
  };

  const optimalPaceOnlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          afterBody: function(context) {
            const dataset = context[0]?.dataset;
            const confidenceLabel = dataset?.pointConfidenceLabels?.[context[0].dataIndex];
            if (!confidenceLabel) return '';
            return confidenceLabel;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 24,
        title: {
          display: true,
          text: 'Hours'
        }
      },
    },
  };

  const hoursWorkedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 126,
        title: {
          display: true,
          text: 'Hours'
        }
      },
    },
  };

  const weeklyHoursAndCapacityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 126,
        title: {
          display: true,
          text: 'Hours'
        }
      },
    },
  };

  const arousalChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          afterBody: function(context) {
            const chartData = prepareArousalChartData();
            if (!chartData || !chartData.arousalZoneData) return '';
            
            const dataIndex = context[0].dataIndex;
            const arousalZone = chartData.arousalZoneData[dataIndex];
            const arousalData = chartData.arousalData[dataIndex];
            
            if (!arousalZone || !arousalData) return '';
            
            const lines = [];
            lines.push('');
            
            // Add mental reserves status
            const zoneStatusText = arousalZone.zoneStatus === 'optimal' ? 'Optimal Zone' : 
                                 arousalZone.zoneStatus === 'under' ? 'Low Mental Reserves' : 'High Mental Reserves';
            const zoneColor = arousalZone.zoneStatus === 'optimal' ? '🟢' : 
                             arousalZone.zoneStatus === 'under' ? '🟡' : '🔴';
            lines.push(`Mental Reserves Status: ${zoneColor} ${zoneStatusText} (${arousalData.mentalReserves.toFixed(1)})`);
            lines.push(`Mental Reserves: ${arousalData.mentalReserves.toFixed(1)}`);
            lines.push(`Optimal Zone Target: ${arousalData.targetMentalReserves.toFixed(1)}`);
            lines.push(`Optimal Range: ${(10 - arousalZone.optimalRange.max).toFixed(1)}-${(10 - arousalZone.optimalRange.min).toFixed(1)} (Pressure: ${arousalZone.actualPressure})`);
            
            return lines;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        title: {
          display: true,
          text: 'Mental Reserves Level'
        }
      },
    },
  };

  const dualAxisOptions = {
    ...chartOptions,
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Hours Worked'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        max: 10,
        title: {
          display: true,
          text: 'Pressure Score'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 10,
        title: {
          display: true,
          text: 'Pressure'
        }
      },
      y: {
        beginAtZero: true,
        max: 10,
        title: { display: false }
      },
    },
  };

  const pressureChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        title: {
          display: true,
          text: 'Pressure Score'
        }
      },
    },
  };

  const emotionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = '#000000';
            });
            return labels;
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const datasetLabel = context.dataset.label;
            const value = context.parsed.y;
            return `${datasetLabel}: ${value.toFixed(1)}`;
          },
          footer: function(context) {
            const total = context.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total Mental Reserves: ${total.toFixed(1)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        beginAtZero: true,
        max: 70,
        stacked: true,
        title: {
          display: true,
          text: 'Mental Reserves Level'
        }
      },
    },
  };

  const mentalResilienceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    layout: {
      padding: { top: 10 },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Mental Resilience Score'
        }
      },
    },
  };

  const refreshRateChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Refresh Rate: ${context.parsed.y.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Refresh Rate (%)'
        }
      },
    },
  };

  const capacityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return value != null ? `Capacity: ${value}` : 'No data';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 126,
        title: {
          display: true,
          text: 'Capacity (sum of last 7 Optimal Paces)'
        }
      },
    },
  };

  const performanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return value != null ? `Performance: ${value}` : 'No data';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 180,
        title: {
          display: true,
          text: 'Performance Score'
        }
      },
    },
  };

  const performanceDebtChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    datasets: { line: { spanGaps: true } },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 1,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            labels.forEach(label => {
              label.borderWidth = 0;
              label.backgroundColor = 'transparent';
            });
            return labels;
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return value != null ? `Performance Debt: ${value}` : 'No data';
          }
        }
      }
    },
    scales: {
      y: {
        min: undefined,
        max: undefined,
        title: {
          display: true,
          text: 'Hours'
        }
      },
    },
  };

  return (
    <Container>
      {(!userData || showUserInfo) && (
        <>
          <SectionHeader>
            <SectionTitle>User Lookup by Shortcode or User ID</SectionTitle>
          </SectionHeader>

          <InputGroup>
            <Input
              type="text"
              placeholder="Shortcode (e.g. A7B2C9) or founders-first user ID"
              value={lookupInput}
              onChange={(e) => {
                const next = e.target.value;
                // Only force uppercase once the value is clearly a 6-char shortcode
                setLookupInput(looksLikeInsatiableShortcode(next) ? next.toUpperCase() : next);
              }}
              disabled={loading}
              style={{ minWidth: 360 }}
            />
            <Button 
              variant="primary" 
              onClick={handleLookup}
              disabled={loading}
            >
              {loading ? 'Looking up...' : 'Lookup User'}
            </Button>
          </InputGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </>
      )}

      {loading && (
        <LoadingMessage>Fetching user data...</LoadingMessage>
      )}

      {userData && (
        <>
          <SectionHeader style={{ marginTop: showUserInfo ? 0 : theme.spacing.lg }}>
            <SectionTitle style={{ margin: 0 }}>
              {showUserInfo ? 'User Information' : 'Charts & Data'}
            </SectionTitle>
            <Button
              variant={showUserInfo ? undefined : 'primary'}
              onClick={() => setShowUserInfo(!showUserInfo)}
            >
              {showUserInfo ? 'Hide User Information' : 'Show User Information'}
            </Button>
          </SectionHeader>
          {showUserInfo && (
          <UserInfo>
            <UserInfoGrid>
              <InfoItem>
                <InfoLabel>Shortcode</InfoLabel>
                <InfoValue>{userData.shortcode || 'Not linked'}</InfoValue>
              </InfoItem>
              {userData.foundersFirstUserId && (
                <InfoItem>
                  <InfoLabel>Founders-First User ID</InfoLabel>
                  <InfoValue style={{ wordBreak: 'break-all' }}>{userData.foundersFirstUserId}</InfoValue>
                </InfoItem>
              )}
              <InfoItem>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>{userData.userInfo?.name || 'Not provided'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Contact Info</InfoLabel>
                <InfoValue style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                  <span>{userData.userInfo?.email || 'Not provided'}</span>
                  <span>{userData.userInfo?.phone || 'Not provided'}</span>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Total Journal Entries</InfoLabel>
                <InfoValue>{userData.journalEntries?.length || 0}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Start Date</InfoLabel>
                <InfoValue>
                  {startDateDisplay}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Active</InfoLabel>
                <InfoValue>
                  {lastActiveDisplay}
                </InfoValue>
              </InfoItem>
              {billableDaysMetric?.label != null && (
                <InfoItem>
                  <InfoLabel>{billableDaysMetric.label}</InfoLabel>
                  <InfoValue>
                    {billableDaysMetric.value}
                    {billableDaysMetric.value === 1 ? ' day' : ' days'} without check-in
                    {billableDaysMetric.missingDates?.length > 0 && (
                      <span style={{ display: 'block', marginTop: 4, fontSize: theme.typography.sizes.small, color: theme.colors.textLight }}>
                        {billableDaysMetric.missingDates.join(', ')}
                      </span>
                    )}
                  </InfoValue>
                </InfoItem>
              )}
          </UserInfoGrid>
          </UserInfo>
          )}

          {userData.journalEntries && userData.journalEntries.length > 0 && (
            <>
              <ChartContainer>
                <ChartTitle>Optimal Pace</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareOptimalPaceOnlyChartData() ? (
                    <Line data={prepareOptimalPaceOnlyChartData()} options={optimalPaceOnlyChartOptions} />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Weekly Hours Worked &amp; Capacity</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareWeeklyHoursAndCapacityChartData() ? (
                    <Line data={prepareWeeklyHoursAndCapacityChartData()} options={weeklyHoursAndCapacityChartOptions} />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Optimal Pace vs Hours Worked Daily</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareOptimalPaceChartData() ? (
                    <Line data={prepareOptimalPaceChartData()} options={chartOptions} />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Performance Debt</ChartTitle>
                <div style={{ height: '300px' }}>
                  {preparePerformanceDebtChartData() ? (
                    <Line data={preparePerformanceDebtChartData()} options={performanceDebtChartOptions} />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Optimal Productivity Zone</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareArousalChartData() ? (
                    <Line data={prepareArousalChartData()} options={arousalChartOptions} />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Mental Reserves</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareEmotionScoreChartData() && (
                    <Bar data={prepareEmotionScoreChartData()} options={emotionChartOptions} />
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Performance</ChartTitle>
                <div style={{ height: '300px' }}>
                  {preparePerformanceChartData() ? (
                    <Line data={preparePerformanceChartData()} options={performanceChartOptions} />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: theme.colors.textLight,
                      fontFamily: theme.typography.bodyFont
                    }}>
                      No journal entries with hours worked and emotional data available
                    </div>
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Pressure Score</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareHoursPressureChartData() && (
                    <Line data={prepareHoursPressureChartData()} options={pressureChartOptions} />
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Mental Resilience Line</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareRegressionChartData() && (
                    <Scatter data={prepareRegressionChartData()} options={scatterOptions} />
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Mental Resilience</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareMentalResilienceChartData() && (
                    <Line data={prepareMentalResilienceChartData()} options={mentalResilienceChartOptions} />
                  )}
                </div>
              </ChartContainer>

              <ChartContainer>
                <ChartTitle>Refresh Rate</ChartTitle>
                <div style={{ height: '300px' }}>
                  {prepareRefreshRateChartData() && (
                    <Line data={prepareRefreshRateChartData()} options={refreshRateChartOptions} />
                  )}
                </div>
              </ChartContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default UserLookupScreen;
